import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { AdminStatsProvider } from "@/context/AdminStatsContext";
import { ConfirmProvider } from "@/components/ConfirmDialog";
import ProtectedRoute from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import DriverRegister from "@/pages/DriverRegister";
import About from "@/pages/About";
import Home from "@/pages/Home";
import BookingsPage from "@/pages/BookingsPage";
import MyBookings from "@/pages/MyBookings";
import NewBooking from "@/pages/NewBooking";
import DriversPage from "@/pages/DriversPage";
import ExternalDrivers from "@/pages/ExternalDrivers";
import RoutePricing from "@/pages/RoutePricing";
import Statements from "@/pages/Statements";
import CarRevenue from "@/pages/CarRevenue";
import FinancePage from "@/pages/FinancePage";
import CompaniesAdmin from "@/pages/CompaniesAdmin";
import CompanyDetail from "@/pages/CompanyDetail";
import NotificationsPage from "@/pages/NotificationsPage";
import CarsPage from "@/pages/CarsPage";
import Account from "@/pages/Account";
import AdminPanel from "@/pages/AdminPanel";
import BookingDetails from "@/pages/BookingDetails";
import HelpPage from "@/pages/HelpPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import DriverHelp from "@/pages/DriverHelp";
import ClientHelp from "@/pages/ClientHelp";
import CarDetails from "@/pages/CarDetails";
import CompanyClients from "@/pages/companies/Clients";
import AdminSettings from "@/pages/AdminSettings";
import FleetVehicles from "@/pages/FleetVehicles";
import CarProfit from "@/pages/CarProfit";
import AdminTeam from "@/pages/AdminTeam";
import AdminChangeRequests from "@/pages/admin/ChangeRequests";
import DriverWageRules from "@/pages/admin/DriverWageRules";
import ExternalDriverWageRules from "@/pages/admin/ExternalDriverWageRules";
import DepartureRules from "@/pages/admin/DepartureRules";
import DataReset from "@/pages/admin/DataReset";
import PricingTemplates from "@/pages/admin/PricingTemplates";
import CompanyMyRequests from "@/pages/companies/MyRequests";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import SWUpdatePrompt from "@/components/SWUpdatePrompt";

function App() {
  return (
    <AuthProvider>
      <AdminStatsProvider>
        <ConfirmProvider>
          <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/driver-register" element={<DriverRegister />} />
          <Route path="/driver/register" element={<DriverRegister />} />
          <Route path="/about" element={<About />} />
          {/* Legacy redirect from the old admin-scoped path */}
          <Route path="/admin/about" element={<Navigate to="/about" replace />} />

          {/* Company / driver / admin shared */}
          <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
          <Route path="/my-bookings" element={<ProtectedRoute><MyBookings /></ProtectedRoute>} />
          <Route path="/booking/:id" element={<ProtectedRoute><BookingDetails /></ProtectedRoute>} />
          <Route path="/car/:id" element={<ProtectedRoute><CarDetails /></ProtectedRoute>} />
          <Route path="/new-booking" element={<ProtectedRoute><NewBooking /></ProtectedRoute>} />
          <Route path="/booking/new" element={<ProtectedRoute><NewBooking /></ProtectedRoute>} />
          <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="/companies/clients" element={<ProtectedRoute><CompanyClients /></ProtectedRoute>} />
          <Route path="/company/clients" element={<ProtectedRoute><CompanyClients /></ProtectedRoute>} />

          {/* Admin-only */}
          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPanel /></ProtectedRoute>} />
          <Route path="/company-bookings" element={<ProtectedRoute adminOnly><BookingsPage /></ProtectedRoute>} />
          <Route path="/admin/companies" element={<ProtectedRoute adminOnly><CompaniesAdmin /></ProtectedRoute>} />
          <Route path="/admin/companies/:id" element={<ProtectedRoute adminOnly><CompanyDetail /></ProtectedRoute>} />
          <Route path="/admin/drivers" element={<ProtectedRoute adminOnly><DriversPage /></ProtectedRoute>} />
          <Route path="/admin/external-drivers" element={<ProtectedRoute adminOnly><ExternalDrivers /></ProtectedRoute>} />
          <Route path="/admin/route-pricing" element={<ProtectedRoute adminOnly><RoutePricing /></ProtectedRoute>} />
          <Route path="/admin/pricing-templates" element={<ProtectedRoute adminOnly><PricingTemplates /></ProtectedRoute>} />
          <Route path="/admin/statements" element={<ProtectedRoute adminOnly><Statements /></ProtectedRoute>} />
          <Route path="/admin/car-revenue" element={<ProtectedRoute adminOnly><CarRevenue /></ProtectedRoute>} />
          <Route path="/admin/finance" element={<ProtectedRoute adminOnly><FinancePage /></ProtectedRoute>} />
          <Route path="/admin/notifications" element={<ProtectedRoute adminOnly><NotificationsPage /></ProtectedRoute>} />
          <Route path="/admin/cars" element={<ProtectedRoute adminOnly><CarsPage /></ProtectedRoute>} />
          <Route path="/admin/fleet/:carType" element={<ProtectedRoute adminOnly><FleetVehicles /></ProtectedRoute>} />
          <Route path="/admin/car-profit" element={<ProtectedRoute adminOnly><CarProfit /></ProtectedRoute>} />
          <Route path="/admin/help" element={<ProtectedRoute adminOnly><HelpPage /></ProtectedRoute>} />
          <Route path="/admin/analytics" element={<ProtectedRoute adminOnly><AnalyticsPage /></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute adminOnly><AdminSettings /></ProtectedRoute>} />
          <Route path="/admin/team" element={<ProtectedRoute adminOnly><AdminTeam /></ProtectedRoute>} />
          <Route path="/admin/change-requests" element={<ProtectedRoute adminOnly><AdminChangeRequests /></ProtectedRoute>} />
          <Route path="/admin/driver-wage-rules" element={<ProtectedRoute adminOnly><DriverWageRules /></ProtectedRoute>} />
          <Route path="/admin/external-driver-wage-rules" element={<ProtectedRoute adminOnly><ExternalDriverWageRules /></ProtectedRoute>} />
          <Route path="/admin/departure-rules" element={<ProtectedRoute adminOnly><DepartureRules /></ProtectedRoute>} />
          <Route path="/admin/data-reset" element={<ProtectedRoute adminOnly><DataReset /></ProtectedRoute>} />
          <Route path="/company/my-requests" element={<ProtectedRoute><CompanyMyRequests /></ProtectedRoute>} />
          <Route path="/driver/help" element={<ProtectedRoute><DriverHelp /></ProtectedRoute>} />
          <Route path="/help" element={<ProtectedRoute><ClientHelp /></ProtectedRoute>} />

          {/* Convenience redirect: /bookings → user's own bookings (Cards) */}
          <Route path="/bookings" element={<Navigate to="/my-bookings" replace />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <PWAInstallPrompt />
      <SWUpdatePrompt />
        </ConfirmProvider>
      </AdminStatsProvider>
      <Toaster
        theme="dark"
        position="top-center"
        toastOptions={{
          style: {
            background: "#0F1F42",
            border: "1px solid rgba(201,169,97,0.35)",
            color: "#fff",
            fontFamily: "Cairo, sans-serif",
          },
        }}
      />
    </AuthProvider>
  );
}

export default App;
